import store from 'redux/store';
import { isEmpty, forEach, keys } from 'lodash-es';
import * as cdLogger from './cdLogger';
import { setCustomStyles, setPreviewConfig, setSdkParams } from 'redux/actions/global';
import { updateDemoCardDetails } from 'redux/actions/demo';
import { hasMerchantTriggeredSdkSubmit } from 'redux/actions/checkout';
import { updateApplePaySupportedDevice } from 'redux/actions/digitalWalletProviders';
import { serverLang2Client, changeLanguage } from 'services/i18n';
import { PREVIEW_INCOMING_MESSAGES } from 'components/containers/Preview/HostedPagePreview';
import { sanitizeNestedObjectValues } from 'utilities/global';
import { parseErrorToSdk } from 'services/errorHandler';
import { HOSTED_PAGE_MODES, LANGS, SDK_EVENT_BY_PARAM } from 'consts';
import { trackSdkParam } from 'utilities/analytics/checkout';

export const sendParentMessage = (type, data = {}) => {
  const message = { type, data };
  window.parent.postMessage(message, '*');
};

export const sendIframeMessageFromParent = (iframId, type, data = {}) => {
  const iframe = document.querySelector(`iframe[data-iframe-id=${iframId}]`);

  const message = { type, data };
  if (iframe) {
    iframe?.contentWindow.postMessage(message, '*');
  }
};

export const sendErrorMessage = ({ type, error = '', meta = { closeIframe: false } }) => {
  sendParentMessage(type, {
    data: parseErrorToSdk(error),
    meta
  });
};

export const sendLoadingEvent = ({ pageType, isLoading }) => {
  sendParentMessage(iframeMessages.ON_LOADING, {
    type: pageType,
    data: {
      isLoading
    }
  });
};

export const sendGetPageFailMessage =
  pageType =>
  (error = '') => {
    const { global } = store.getState();
    if (!global.isSdk) return;

    sendParentMessage(iframeMessages.ON_GET_PAGE_FAIL, {
      type: pageType,
      data: parseErrorToSdk(error || 'No token'),
      meta: { closeIframe: true }
    });
  };

export const sendStartMessage = pageType => () => {
  sendLoadingEvent({ pageType, isLoading: true });
};

export const sendSuccessMessage =
  pageType =>
  ({ eventType, data, meta = { closeIframe: false, withoutLoading: false } }) => {
    sendParentMessage(eventType, {
      data,
      meta
    });
    if (meta?.withoutLoading) return;
    sendLoadingEvent({ pageType, isLoading: false });
  };

export const sendFailMessage =
  pageType =>
  ({ eventType, error, meta }) => {
    sendErrorMessage({ type: eventType, error, meta });
    if (meta?.withoutLoading) return;
    sendLoadingEvent({ pageType, isLoading: false });
  };

export const getDefaultMessages = pageType => {
  return {
    sendGetPageFailMsg: sendGetPageFailMessage(pageType),
    sendStartMsg: sendStartMessage(pageType),
    sendSuccessMsg: sendSuccessMessage(pageType),
    sendFailMsg: sendFailMessage(pageType)
  };
};

export const listenMessage = mode => {
  let handler = handleMessage;

  if (mode === HOSTED_PAGE_MODES.preview) {
    handler = handlePreviewMessage;
  }

  window.addEventListener('message', handler, false);
};

let handleMessage = event => {
  try {
    const message = event.data;
    switch (message && message.type) {
      case 'PARAMS': {
        if (!message.data) break;
        const data = {
          mobileView: message.data.mobileView,
          btnColor: message.data.btnColor,
          btnText: message.data.btnText,
          saveCardDefault: message.data.saveCardDefault,
          hideCartItems: message.data.hideCartItems,
          hideOrderDetails: message.data.hideOrderDetails,
          hostname: message.data.hostname,
          enableRedirect: message.data.redirect,
          hideSubmitButton: message.data.hideSubmitButton,
          waitOnPaymentConfirmation: message.data.waitOnPaymentConfirmation,
          closeOnComplete: message.data.closeOnComplete,
          waitOnPaymentRedirect: message.data.waitOnPaymentRedirect
        };

        const { customStyles = {} } = message.data;

        customStylesSanitizer(customStyles);

        store.dispatch(setSdkParams(data));
        store.dispatch(setCustomStyles(customStyles));

        forEach(keys(SDK_EVENT_BY_PARAM), param =>
          trackSdkParam(SDK_EVENT_BY_PARAM[param], SDK_EVENT_BY_PARAM[param], data[param])
        );
        break;
      }
      case 'INIT_DIGITAL_PROVIDERS': {
        if (!message.data) break;
        const { apple_pay_supported } = message.data;
        store.dispatch(updateApplePaySupportedDevice({ supported: apple_pay_supported }));
        break;
      }
      case 'CHANGE_LANGUAGE': {
        if (!message.data) break;
        const { language = '' } = message?.data || {};
        const parsedLanguage = serverLang2Client(language);

        if (!LANGS[parsedLanguage]) break;

        changeLanguage(parsedLanguage);
        break;
      }
      case 'CHECKOUT_SUBMIT_PAYMENT': {
        store.dispatch(hasMerchantTriggeredSdkSubmit(true));
        break;
      }
    }
  } catch (e) {
    cdLogger.error(e);
  }
};

const handlePreviewMessage = event => {
  try {
    const message = event.data;

    switch (message && message.type) {
      case PREVIEW_INCOMING_MESSAGES.PARAMS: {
        if (!message.data) break;
        const {
          global: { isSdk }
        } = store.getState();

        const {
          ctaText,
          logo,
          buttonColor: btnColor,
          categories,
          customStyles = {},
          reusablePage,
          checkoutPage,
          alias,
          language,
          previewApplePay,
          previewGooglePay
        } = message.data;
        const data = {
          ctaText,
          logo,
          btnColor,
          categories,
          reusablePage,
          checkoutPage,
          alias,
          language,
          previewApplePay,
          previewGooglePay
        };

        if (customStyles && Object.keys(customStyles).length && isSdk) {
          store.dispatch(setCustomStyles(customStyles));
        }

        store.dispatch(setPreviewConfig(data));
        break;
      }
      case PREVIEW_INCOMING_MESSAGES.SET_CARD_DETAILS: {
        if (!message.data) break;

        store.dispatch(updateDemoCardDetails(message.data));
        break;
      }
      default:
        break;
    }
  } catch (e) {
    cdLogger.error(e);
  }
};

export const handleSdkErrors = (error = {}) => {
  const { global } = store.getState();
  if (isEmpty(error) || !global.isSdk) return;

  sendParentMessage(iframeMessages.ON_FAILURE, { error });
};

const customStylesSanitizer = (receivedStyles = {}) => {
  const dangerousValues = ['url('];
  sanitizeNestedObjectValues(receivedStyles, dangerousValues);
};

const GLOBAL_MESSAGES = {
  IS_MOBILE: 'IS_MOBILE',
  GET_PARAMS: 'GET_PARAMS',
  CLOSE_IFRAME: 'CLOSE_IFRAME',
  ON_RESIZE: 'ON_RESIZE',
  ON_LOADING: 'ON_LOADING',
  ON_FAILURE: 'ON_FAILURE',
  ON_GET_PAGE_FAIL: 'ON_GET_PAGE_FAIL'
};

const CHECKOUT_MESSAGES = {
  CHECKOUT_ON_FAILURE: 'CHECKOUT_ON_FAILURE',
  CHECKOUT_CREATE_PAYMENT_SUCCESS: 'CHECKOUT_CREATE_PAYMENT_SUCCESS',
  CHECKOUT_CREATE_PAYMENT_FAIL: 'CHECKOUT_CREATE_PAYMENT_FAIL',
  CHECKOUT_EDIT_CARD_SUCCESS: 'CHECKOUT_EDIT_CARD_SUCCESS',
  CHECKOUT_EDIT_CARD_FAIL: 'CHECKOUT_EDIT_CARD_FAIL',
  CHECKOUT_DELETE_CARD_SUCCESS: 'CHECKOUT_DELETE_CARD_SUCCESS',
  CHECKOUT_DELETE_CARD_FAIL: 'CHECKOUT_DELETE_CARD_FAIL',
  CHECKOUT_PAYMENT_PENDING: 'CHECKOUT_PAYMENT_PENDING',
  CHECKOUT_PAYMENT_EXPIRED: 'CHECKOUT_PAYMENT_EXPIRED'
};

const CARD_TOKENIZATION_MESSAGES = {
  CARD_TOKENIZATION_ON_FAILURE: 'CARD_TOKENIZATION_ON_FAILURE',
  CARD_TOKENIZATION_CREATE_SUCCESS: 'CARD_TOKENIZATION_CREATE_SUCCESS',
  CARD_TOKENIZATION_CREATE_PENDING: 'CARD_TOKENIZATION_CREATE_PENDING',
  CARD_TOKENIZATION_CREATE_FAIL: 'CARD_TOKENIZATION_CREATE_FAIL',
  CARD_TOKENIZATION_ON_UPDATE: 'CARD_TOKENIZATION_ON_UPDATE'
};

const BENEFICIARY_TOKENIZATION_MESSAGES = {
  BENEFICIARY_TOKENIZATION_ON_FAILURE: 'BENEFICIARY_TOKENIZATION_ON_FAILURE',
  BENEFICIARY_CREATE_SUCCESS: 'BENEFICIARY_CREATE_SUCCESS',
  BENEFICIARY_CREATE_FAIL: 'BENEFICIARY_CREATE_FAIL'
};

const ISSUING_MESSAGES = {
  ISSUING_ON_FAILURE: 'ISSUING_ON_FAILURE',
  ISSUING_GET_ISSUED_CARD_SUCCESS: 'ISSUING_GET_ISSUED_CARD_SUCCESS',
  ISSUING_GET_ISSUED_CARD_FAIL: 'ISSUING_GET_ISSUED_CARD_FAIL',
  ISSUING_ISSUED_CARD_EXPIRED: 'ISSUING_ISSUED_CARD_EXPIRED'
};

const IDV_MESSAGES = {
  IDV_ON_FAILURE: 'IDV_ON_FAILURE',
  IDV_SUBMIT_SUCCESS: 'IDV_SUBMIT_SUCCESS',
  IDV_SUBMIT_FAIL: 'IDV_SUBMIT_FAIL',
  IDV_LIVENESS_CHECK_SUCCESS: 'IDV_LIVENESS_CHECK_SUCCESS',
  IDV_LIVENESS_CHECK_FAIL: 'IDV_LIVENESS_CHECK_FAIL'
};

const CARD_TO_CARD_TOKENIZE_MESSAGES = {
  ADD_SOURCE_CARD_ON_FAILURE: 'ADD_SOURCE_CARD_ON_FAILURE',
  ADD_SOURCE_CARD_SUCCESS: 'ADD_SOURCE_CARD_SUCCESS',
  ADD_SOURCE_CARD_FAIL: 'ADD_SOURCE_CARD_FAIL',
  ADD_SOURCE_CARD_REDIRECT_SUCCESS: 'ADD_SOURCE_CARD_REDIRECT_SUCCESS',
  ADD_SOURCE_CARD_REDIRECT_FAIL: 'ADD_SOURCE_CARD_REDIRECT_FAIL'
};

const CARD_TO_CARD_TRANSFER_MESSAGES = {
  TRANSFER_CARD_TO_CARD_FUNDS_SUCCESS: 'TRANSFER_CARD_TO_CARD_FUNDS_SUCCESS',
  TRANSFER_CARD_TO_CARD_FUNDS_FAIL: 'TRANSFER_CARD_TO_CARD_FUNDS_FAIL',
  TRANSFER_CARD_TO_CARD_FUNDS_3DS_SUCCESS: 'TRANSFER_CARD_TO_CARD_FUNDS_3DS_SUCCESS',
  TRANSFER_CARD_TO_CARD_FUNDS_3DS_FAIL: 'TRANSFER_CARD_TO_CARD_FUNDS_3DS_FAIL',
  TRANSFER_CARD_TO_CARD_FUNDS_3DS_PENDING: 'TRANSFER_CARD_TO_CARD_FUNDS_3DS_PENDING'
};

const RESET_PIN_MESSAGES = {
  RESET_PIN_SUCCESS: 'RESET_PIN_SUCCESS',
  RESET_PIN_FAIL: 'RESET_PIN_FAIL'
};

const VIEW_PIN_MESSAGES = {
  VIEW_PIN_SUCCESS: 'VIEW_PIN_SUCCESS',
  VIEW_PIN_FAIL: 'VIEW_PIN_FAIL',
  VIEW_PIN_SET_TIME_OUT: 'VIEW_PIN_SET_TIME_OUT'
};

const ACTIVATE_ISSUE_CARD_MESSAGES = {
  ACTIVATE_ISSUE_CARD_SUCCESS: 'ACTIVATE_ISSUE_CARD_SUCCESS',
  ACTIVATE_ISSUE_CARD_FAIL: 'ACTIVATE_ISSUE_CARD_FAIL',
  ERROR_HOSTED_PAGE_SET_PIN: 'ERROR_HOSTED_PAGE_SET_PIN'
};

const OTP_MESSAGES = {
  VERIFY_OTP_FAIL: 'VERIFY_OTP_FAIL'
};

export const iframeMessages = {
  ...GLOBAL_MESSAGES,
  ...CHECKOUT_MESSAGES,
  ...CARD_TOKENIZATION_MESSAGES,
  ...BENEFICIARY_TOKENIZATION_MESSAGES,
  ...ISSUING_MESSAGES,
  ...IDV_MESSAGES,
  ...CARD_TO_CARD_TOKENIZE_MESSAGES,
  ...CARD_TO_CARD_TRANSFER_MESSAGES,
  ...RESET_PIN_MESSAGES,
  ...VIEW_PIN_MESSAGES,
  ...ACTIVATE_ISSUE_CARD_MESSAGES,
  ...OTP_MESSAGES
};
